import React, { useMemo, useCallback, useEffect } from 'react';
import { produce } from 'immer';
import TabItem from './TabItem';
import { useCommunicationStore, getStore } from '../../../stores/StoreContext';

/**
 * CommunicationTab Component
 * 
 * A specialized tab component for the Communication/Chat tab that manages
 * its own subscription to the unread message count. This prevents the parent
 * SessionDetails component from re-rendering when the unread count changes.
 * 
 * Automatically resets the unread count when the tab becomes selected.
 * 
 * Uses Immer for immutable state updates to ensure optimal performance.
 * 
 * @param {Object} props
 * @param {string} props.tabKey - Unique identifier for the tab (should be 'communication')
 * @param {Object} props.option - Base tab configuration object
 * @param {string} props.option.label - Display label for the tab
 * @param {boolean} props.isSelected - Whether this tab is currently selected
 * @param {Function} props.onClick - Click handler for the tab
 */
function CommunicationTab({ tabKey, option, isSelected, onClick }) {
    // Subscribe to unread message count only in this component
    // This prevents parent re-renders when the count changes
    const unseenChatMessageCount = useCommunicationStore((state) => state.unseenChatMessageCount);

    // Get the communication store instance for additional functionality if needed
    const CommunicationStore = getStore('communicationStore');

    // Reset unread count when tab becomes selected
    useEffect(() => {
        if (isSelected && unseenChatMessageCount > 0) {
            CommunicationStore.setUnseenChatMessageCount(0);
        }
    }, [isSelected, unseenChatMessageCount, CommunicationStore]);

    // Create an enhanced option with the live unread count using Immer
    const enhancedOption = useMemo(
        () =>
            produce(option, (draft) => {
                if (!draft.notification) {
                    draft.notification = {};
                }
                draft.notification.count = unseenChatMessageCount;
            }),
        [option, unseenChatMessageCount]
    );

    return (
        <TabItem
            tabKey={tabKey}
            option={enhancedOption}
            isSelected={isSelected}
            onClick={onClick}
        />
    );
}

export default CommunicationTab;
